// =============================================================
// chain.ts — ChainClient: 컨트랙트 인스턴스 + read/write 단일 통로
// =============================================================

import { Contract, type BrowserProvider } from 'ethers';
import { PV_GATE_ADDRESS, PV_GATE_ABI, ERC20_ABI, ZERO } from './config';
import { SdkError, type WalletAdapter } from './types';

export function hasAddress(a: string | null | undefined): a is string {
  return !!a && a !== ZERO;
}

export class ChainClient {
  constructor(private wallet: WalletAdapter) {}

  private async provider(): Promise<BrowserProvider> {
    return this.wallet.getProvider();
  }

  /** read 전용 PvGate 인스턴스 */
  async gateRead(): Promise<Contract> {
    if (!hasAddress(PV_GATE_ADDRESS)) {
      throw new SdkError('NO_GATE_ADDRESS', 'PV_GATE_ADDRESS 미설정');
    }
    return new Contract(PV_GATE_ADDRESS, PV_GATE_ABI, await this.provider());
  }

  /** write(서명) PvGate 인스턴스 */
  async gateWrite(): Promise<Contract> {
    if (!hasAddress(PV_GATE_ADDRESS)) {
      throw new SdkError('NO_GATE_ADDRESS', 'PV_GATE_ADDRESS 미설정');
    }
    const signer = await (await this.provider()).getSigner();
    return new Contract(PV_GATE_ADDRESS, PV_GATE_ABI, signer);
  }

  async erc20(address: string): Promise<Contract> {
    return new Contract(address, ERC20_ABI, await this.provider());
  }

  async bnbBalance(address: string): Promise<bigint> {
    return (await this.provider()).getBalance(address);
  }

  /**
   * MetaMask 등 지갑에 MOL 토큰을 등록(watch)하도록 요청한다. (EIP-747)
   *  · ERC20 은 지갑이 자동으로 목록에 안 띄우므로, 스왑 후 수동 등록을 대신 유도.
   *  · MOL 토큰 주소는 게이트 컨트랙트의 mol() 에서 조회한다(별도 주소 불필요).
   *  · 사용자가 지갑 팝업에서 '추가' 를 눌러야 최종 등록됨.
   * @returns 사용자가 추가를 승인했으면 true.
   */
  async watchMol(): Promise<boolean> {
    // 1) MOL 토큰 주소 확보
    const gate = await this.gateRead();
    const molAddr = (await gate.mol()) as string;
    if (!hasAddress(molAddr)) {
      throw new SdkError('UNKNOWN', 'MOL 토큰 주소를 확인할 수 없습니다.');
    }

    // 2) EIP-1193 provider 로 wallet_watchAsset 요청
    const provider = await this.provider();
    const eth = (provider as unknown as {
      provider?: { request(a: { method: string; params: unknown }): Promise<unknown> };
    }).provider;
    const req =
      eth?.request ??
      (window as unknown as {
        ethereum?: { request(a: { method: string; params: unknown }): Promise<unknown> };
      }).ethereum?.request;
    if (!req) throw new SdkError('NO_WALLET', '지갑을 찾을 수 없습니다.');

    const added = (await req({
      method: 'wallet_watchAsset',
      params: {
        type: 'ERC20',
        options: {
          address: molAddr,
          symbol: 'MOL',
          decimals: 18,
          image: `${window.location.origin}/molepin.png`,
        },
      },
    })) as boolean;
    return !!added;
  }
}